import { createCustomComparisonMultiProvider } from '@/app-shell/flags/custom-comparison-strategy';
import {
  createFlagEvaluationTelemetryHook,
  DEFAULTS_ONLY_PROVIDER_NAME,
  FLAGSMITH_PROVIDER_NAME,
  OFREP_PROVIDER_NAME,
  registerFlagProviderName,
} from '@/app-shell/flags/flag-evaluation-telemetry';
import { createFlagsmithProvider } from '@/app-shell/flags/flagsmith-provider';
import { createLaunchDarklyEvaluationReportHook } from '@/app-shell/flags/launchdarkly-evaluation-reports';
import { createOfrepProvider } from '@/app-shell/flags/ofrep-provider';
import { type OverrideOpenFeatureProvider } from '@/features/flags-dev/openfeature-override-provider';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { OpenFeature } from '@openfeature/react-sdk';
import { InMemoryProvider, type Provider } from '@openfeature/web-sdk';

/**
 * Cloud flag-provider registration — app wiring for the
 * `@heidi/runtime/feature-flags` door (P3 F3; design D4/D9). The door exposes
 * NO registration API: this module is where providers are chosen,
 * constructed, and installed, so a build variant's disabled path is ABSENCE,
 * not a runtime check inside the package. On-prem flag evaluation is fed by
 * the in-process bundle provider instead (`onprem-bundle-provider.tsx`,
 * mounted via `OnPremFlagBundleSync`).
 *
 * Called synchronously from `_app.tsx` module scope (browser only). Provider
 * install must NOT be deferred/async — boot-gated consumers (e.g.
 * ThemeManager) read flags on first paint, and an async install would race
 * them to the code default.
 */
export function registerFlagProviders(options: {
  /**
   * Dev-tools wrapper class (non-production builds only — `_app.tsx` loads it
   * behind its env-gated conditional `require`, so it is `undefined` in
   * production). When present, wraps the Flagsmith provider so sessionStorage
   * overrides intercept resolve* calls and re-emit ConfigurationChanged for
   * live `use*FlagValue` hooks.
   */
  overrideOpenFeatureProvider?: typeof OverrideOpenFeatureProvider;
  ofrepEnabled?: boolean;
}): void {
  // TELEMETRY-GATE (onprem-guardrails F1): the gate is the belt — key PRESENCE
  // alone must never construct or initialize a phone-home SDK (a leaked
  // NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY would otherwise point the Flagsmith
  // SDK at the sys.heidihealth.com proxy).
  //
  // This reads the telemetry axis rather than `isOnPrem` because the S5 egress
  // smoke caught it: with a key present, a cloud build with telemetry off still
  // reached `sys.heidihealth.com/fs/api/v1/flags/`. "Degrades to code defaults
  // via key absence" is belt without braces, which is what F1 forbids. Flags
  // resolving to code defaults on a suppressed build is the same posture the
  // axis already gives PostHog-sourced flags (design D6).
  //
  // Scope: construct/initialize — the static imports above stay (install must
  // be synchronous, see the doc block); bundle-level removal of the
  // loaded-but-inert SDK modules is the separately tracked ARCFE-664
  // `KNOWN_LEAKS` concern.
  if (!isSaasTelemetryEnabled) return;

  OpenFeature.addHooks(createFlagEvaluationTelemetryHook());

  const flagsmithEnvKey = process.env.NEXT_PUBLIC_FLAGSMITH_ENVIRONMENT_KEY;
  if (!flagsmithEnvKey) {
    // No flag key on a telemetry-enabled build. A defaults-only provider is
    // READY at once, so the readiness gates resolve and every flag serves its
    // call-site default; no SDK is constructed. Named and logged so a deployed
    // build that lost its key shows up on the flag-evaluation dashboard.
    logger.warn('[flags] no flag key configured; serving call-site defaults', {
      failure: 'no_provider_key',
    });
    registerFlagProviderName(undefined, DEFAULTS_ONLY_PROVIDER_NAME);
    OpenFeature.setProvider(new InMemoryProvider({}));
    return;
  }

  const flagsmithClientProvider = createFlagsmithProvider(flagsmithEnvKey);
  registerFlagProviderName(undefined, FLAGSMITH_PROVIDER_NAME);

  if (options.ofrepEnabled) {
    OpenFeature.addHooks(createLaunchDarklyEvaluationReportHook());
  }

  const provider = options.ofrepEnabled
    ? withOfrepComparison(flagsmithClientProvider)
    : flagsmithClientProvider;

  installProvider(provider, options.overrideOpenFeatureProvider);
}

const withOfrepComparison = (primary: Provider): Provider =>
  createCustomComparisonMultiProvider({
    authorities: [{ provider: primary, name: FLAGSMITH_PROVIDER_NAME }],
    shadow: { provider: createOfrepProvider(), name: OFREP_PROVIDER_NAME },
  });

const installProvider = (
  provider: Provider,
  overrideCtor?: typeof OverrideOpenFeatureProvider
): void => {
  if (!overrideCtor) {
    OpenFeature.setProvider(provider);
    return;
  }
  try {
    OpenFeature.setProvider(new overrideCtor(provider, 'flagsmith'));
  } catch {
    logger.warn('[flags-dev] failed to install override provider', {
      failure: 'registration_failed',
    });
    OpenFeature.setProvider(provider);
  }
};
